import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/Button";
import { devicesQuery } from "../devices/queries";
import { PlansDialog } from "../devices/plans/PlansDialog";
import { devicePlansQuery } from "../devices/plans/queries";
import {
  fillableTypes,
  fromChoice,
  fromPageWidget,
  keyLimit,
  keysFor,
  STATISTICS_TYPES,
  suggestions,
  type PickableType,
  type Suggestion,
} from "../devices/page/picker";
import { defaultPage, profileQuery, type PageDocument } from "../devices/page/profile";
import { devicePageQuery, deviceQuery, type WidgetPage } from "../devices/page/queries";
import { toWidget } from "../devices/page/toWidgets";
import { WidgetPreview } from "../../widgets/WidgetPreview";
import { newWidget, type DocWidget } from "./layout";

/** The widget types whose Values menu adds one more value (DDB-39): a line, a grid value, a tile value, a bar or a lane. */
export type LineType = "line_chart" | "bar_chart" | "value_grid" | "device_tile" | "level" | "state_timeline";

/** The types most people want, offered first; the rest are under More (DDB-35). */
const COMMON: readonly string[] = ["value", "toggle", "gauge", "line_chart", "alarm_list"];

/**
 * Add a widget (WEB-DSH-EDIT-01): pick a device, then one of "Suggested for <device type>" or a widget type the
 * device can fill with the keys to show (device-dashboards WEB-DDB-PICK). With `device` (the device page's editor,
 * WEB-DDB-EDIT-01) the device is fixed and not asked. With `line` it adds one more line to a chart instead (DDB-36):
 * any device, the same kind of value first, and a third kind of value refused (DDB-37); for a value grid, one more value.
 * On a device page a line comes from that device only. With `change` it changes that widget in place (DDB-39, WPK-04):
 * the picker opens at its device, type and values, without the suggestions.
 */
export function AddWidgetDialog({
  open,
  orgId,
  widgets,
  device,
  line,
  change,
  unitOf,
  onAdd,
  onAddLine,
  onChange,
  onClose,
  source,
}: {
  open: boolean;
  orgId: string;
  widgets: DocWidget[];
  device?: string;
  /** The widget a value is added to: its type, the units it shows, and its bindings as `device/datapoint`. */
  line?: { type: LineType; units: (string | null)[]; taken: string[] };
  /** The widget being changed. */
  change?: DocWidget;
  /** The unit of a value the widget shows, by `device/datapoint`: a changed chart keeps other devices' lines. */
  unitOf?: (key: string) => string | null;
  onAdd: (w: DocWidget) => void;
  onAddLine?: (b: { device: string; datapoint: string }, unit: string | null, name: string) => void;
  onChange?: (built: Pick<DocWidget, "type" | "bindings" | "config">, device: string) => void;
  onClose: () => void;
  /** The device's page and profile when they are not read from an organisation: the console's device type (WEB-DTP-03). */
  source?: { page: WidgetPage; doc: PageDocument };
}) {
  const { t, i18n } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  const ids = { title: useId(), device: useId(), type: useId(), suggested: useId(), other: useId() };
  const [deviceId, setDeviceId] = useState("");
  const [type, setType] = useState<PickableType | "">("");
  const [keys, setKeys] = useState<string[]>([]);

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) {
      const at = device ?? change?.bindings?.[0]?.device ?? change?.source?.devices?.[0] ?? "";
      setDeviceId(at);
      setType((change?.type as PickableType | undefined) ?? "");
      setKeys(
        (change?.bindings ?? [])
          .filter((b) => b.device === at)
          .flatMap((b) => b.command ?? b.setting ?? b.datapoint ?? []),
      );
      d.showModal();
    } else if (!open && d.open) d.close();
  }, [open, device, change]);

  const devices = useInfiniteQuery({ ...devicesQuery(orgId, {}), enabled: open && !device });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = devices;
  useEffect(() => {
    if (open && hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);
  const all = useMemo(
    () =>
      (devices.data?.pages.flatMap((p) => p.items) ?? []).sort((a, b) =>
        (a.name ?? a.device_id).localeCompare(b.name ?? b.device_id, i18n.language),
      ),
    [devices.data, i18n.language],
  );

  const page = useQuery({
    ...devicePageQuery(orgId, deviceId, i18n.language),
    enabled: open && !!deviceId && !source,
  });
  const profile = useQuery({ ...profileQuery(orgId, deviceId, i18n.language), enabled: open && !!deviceId && !source });
  // A device whose plan has no statistics lists the Pro widgets greyed, with See plans (PST-03). Plans that cannot be
  // read (a device shared from another organisation) lock nothing: the widget then says so itself (PST-04).
  const plans = useQuery({ ...devicePlansQuery(orgId, deviceId), enabled: open && !!deviceId && !source });
  const currentPlan = plans.data?.find((x) => x.current);
  const statisticsLocked = !!currentPlan && currentPlan.features?.statistics !== true;
  const [plansOpen, setPlansOpen] = useState(false);
  const planDevice = useQuery({ ...deviceQuery(orgId, deviceId), enabled: plansOpen && !!deviceId });
  const p = source?.page ?? page.data;
  // PD-13: a type the device's plan does not allow is greyed with the plan that has it.
  const needs: Record<string, string> = p?.plan_widgets?.needs ?? {};
  const locked = (ty: string) =>
    (statisticsLocked && STATISTICS_TYPES.includes(ty)) || (!!needs[ty] && !STATISTICS_TYPES.includes(ty));
  const doc = source?.doc ?? profile.data;
  const profilePending = !source && profile.isPending;
  const editing = useMemo(
    () =>
      widgets
        .filter((w) => (w.bindings ?? []).some((b) => b.device === deviceId) || w.source?.devices?.includes(deviceId))
        .map(fromPageWidget),
    [widgets, deviceId],
  );
  // A suggested chart with readings of more than two units is not offered (DDB-37).
  const suggested =
    p && !profilePending
      ? suggestions(doc, defaultPage(doc), p, editing).filter(
          (s) =>
            (s.type !== "line_chart" && s.type !== "bar_chart") ||
            new Set((s.datapoints ?? []).map((k) => p.points[k]?.unit ?? null)).size <= 2,
        )
      : [];
  // Device health is about several devices: never on a device page, whose widgets show that device only (WLB-02).
  const types = p && !profilePending ? fillableTypes(doc, p).filter((ty) => !device || ty !== "health") : [];
  const choices = p && type ? keysFor(type, doc, p) : [];
  const isChart = (ty: string | undefined) => ty === "line_chart" || ty === "bar_chart";
  // A chart that stays a chart keeps its lines from other devices (layout `change`): they count against its limits.
  const kept =
    change && isChart(change.type) && isChart(type)
      ? (change.bindings ?? []).filter((b) => b.device !== deviceId && b.datapoint)
      : [];
  const limit = !type ? 0 : kept.length ? Math.max(1, keyLimit(type) - kept.length) : keyLimit(type);
  const typeName = doc?.function?.name ?? doc?.product.name ?? all.find((d) => d.id === deviceId)?.product ?? "";

  function add(s: Suggestion, page: WidgetPage) {
    const built = toWidget({ ...s }, 0, deviceId, page);
    onAdd(newWidget(widgets, { ...built, bindings: built.bindings }, deviceId));
    onClose();
  }
  const label = (s: Suggestion) => {
    const names = [
      ...(s.datapoints ?? []).map((k) => p?.points[k]?.name ?? k),
      ...[...(s.commands ?? []), ...(s.settings ?? [])].map((k) => p?.actions[k]?.name ?? k),
    ];
    return names.length ? `${t(`editor.type.${s.type}`)}: ${names.join(", ")}` : t(`editor.type.${s.type}`);
  };
  const ready = !!type && (limit === 0 || choices.some((c) => keys.includes(c.key)));
  // A chart takes at most two kinds of value (DDB-37): a reading of a third unit is not offered once two are picked.
  const pickedUnits = new Set([
    ...keys.map((k) => p?.points[k]?.unit ?? null),
    ...kept.map((b) => unitOf?.(`${b.device}/${b.datapoint}`) ?? null),
  ]);
  const thirdUnit = (k: string) =>
    (type === "line_chart" || type === "bar_chart") &&
    pickedUnits.size >= 2 &&
    !pickedUnits.has(p?.points[k]?.unit ?? null);
  // Line mode: the chart's readings on this device not on it yet, the same unit first; a third unit is refused.
  const lineChoices =
    line && p
      ? keysFor(line.type, doc, p)
          .filter((c) => !line.taken.includes(`${deviceId}/${c.key}`))
          .map((c) => {
            const unit = p.points[c.key]?.unit ?? null;
            return {
              ...c,
              unit,
              same: line.units.includes(unit),
              refused: isChart(line.type) && !line.units.includes(unit) && line.units.length >= 2,
            };
          })
          .sort((a, b) => Number(b.same) - Number(a.same))
      : [];

  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={ids.title}
      className="m-auto w-full max-w-lg rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <div className="space-y-4 p-6">
        <h2 id={ids.title} className="text-lg font-semibold text-gray-900">
          {line
            ? t(isChart(line.type) ? "editor.addLineTitle" : "editor.addValueTitle")
            : change
              ? t("editor.changeTitle")
              : t("editor.addTitle")}
        </h2>
        <div hidden={!!device}>
          <label htmlFor={ids.device} className="block text-sm font-medium text-gray-900">
            {t("editor.device")}
          </label>
          <select
            id={ids.device}
            value={deviceId}
            onChange={(e) => {
              setDeviceId(e.target.value);
              // A change keeps the way it is shown when the other device can fill it too.
              if (!change) setType("");
              setKeys([]);
            }}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">{t("editor.chooseDevice")}</option>
            {all.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name ?? d.device_id}
              </option>
            ))}
          </select>
        </div>
        {line && deviceId && p && (
          <fieldset>
            <legend className="text-sm text-gray-900">{t("editor.chooseOne")}</legend>
            {lineChoices.length === 0 ? (
              <p className="mt-1 text-sm text-gray-800">{t("editor.noTypes")}</p>
            ) : (
              <ul className="mt-1 max-h-60 space-y-1 overflow-auto">
                {lineChoices.map((c) => (
                  <li key={c.key}>
                    <label className="flex items-center gap-2 text-sm text-gray-900">
                      <input
                        type="radio"
                        name="line"
                        checked={keys[0] === c.key}
                        disabled={c.refused}
                        onChange={() => setKeys([c.key])}
                      />
                      {c.name}
                      {c.unit && <span className="text-xs text-gray-700">{c.unit}</span>}
                    </label>
                  </li>
                ))}
              </ul>
            )}
            {lineChoices.some((c) => c.refused) && <p className="mt-2 text-xs text-gray-800">{t("editor.twoUnits")}</p>}
          </fieldset>
        )}
        {!line && deviceId && p && (
          <>
            {!change && suggested.length > 0 && (
              <section aria-labelledby={ids.suggested}>
                <h3 id={ids.suggested} className="text-sm font-semibold text-gray-900">
                  {t("editor.suggested", { type: typeName })}
                </h3>
                <ul className="mt-1 space-y-1">
                  {suggested.map((s, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => add(s, p)}
                        className="flex w-full items-center gap-3 rounded-md border border-gray-200 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50"
                      >
                        <WidgetPreview type={s.type} className="h-6 w-9 shrink-0 text-brand" />
                        {label(s)}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <section aria-labelledby={ids.other} className="space-y-2">
              <h3 id={ids.other} className="text-sm font-semibold text-gray-900">
                {change ? t("editor.showAs") : t("editor.otherTypes")}
              </h3>
              {types.length === 0 ? (
                <p className="text-sm text-gray-800">{t("editor.noTypes")}</p>
              ) : (
                <>
                  {(
                    [
                      ["common", types.filter((ty) => COMMON.includes(ty))],
                      ["moreTypes", types.filter((ty) => !COMMON.includes(ty))],
                    ] as const
                  ).map(
                    ([group, list]) =>
                      list.length > 0 && (
                        <fieldset key={group}>
                          <legend className="text-xs font-medium text-gray-800">{t(`editor.${group}`)}</legend>
                          <div className="mt-1 grid grid-cols-3 gap-2 sm:grid-cols-4">
                            {list.map((ty) => (
                              <label
                                key={ty}
                                className={`relative flex flex-col items-center gap-1 rounded-md border border-gray-200 p-2 text-center text-xs text-gray-900 has-checked:border-brand has-checked:bg-brand/5 has-checked:ring-2 has-checked:ring-brand has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-brand ${locked(ty) ? "cursor-not-allowed bg-gray-50 text-gray-600" : "cursor-pointer hover:bg-gray-50"}`}
                              >
                                {STATISTICS_TYPES.includes(ty) ? (
                                  <span className="absolute top-1 right-1 rounded-sm bg-brand px-1 text-[0.6rem] font-semibold text-white">
                                    {t("statistics.pro", { ns: "widget" })}
                                  </span>
                                ) : (
                                  needs[ty] && (
                                    <span className="absolute top-1 right-1 rounded-sm bg-brand px-1 text-[0.6rem] font-semibold text-white">
                                      {t(`sharing.plan.${needs[ty]}`, {
                                        defaultValue:
                                          (needs[ty] as string).charAt(0).toUpperCase() +
                                          (needs[ty] as string).slice(1),
                                      })}
                                    </span>
                                  )
                                )}
                                <input
                                  type="radio"
                                  name={ids.type}
                                  value={ty}
                                  checked={type === ty}
                                  disabled={locked(ty)}
                                  onChange={() => {
                                    setType(ty);
                                    setKeys([]);
                                  }}
                                  className="sr-only"
                                />
                                <WidgetPreview type={ty} className="h-8 w-12 text-brand" />
                                {t(`editor.type.${ty}`)}
                              </label>
                            ))}
                          </div>
                        </fieldset>
                      ),
                  )}
                  {types.some(locked) && (
                    <p className="text-xs text-gray-800">
                      <button
                        type="button"
                        onClick={() => setPlansOpen(true)}
                        className="font-medium text-brand underline underline-offset-2"
                      >
                        {t("statistics.availableOnPro", { ns: "widget" })}
                      </button>
                    </p>
                  )}
                  {choices.length > 0 && (
                    <fieldset>
                      <legend className="text-sm text-gray-900">
                        {limit === 1 ? t("editor.chooseOne") : t("editor.chooseUpTo", { count: limit })}
                      </legend>
                      <ul className="mt-1 max-h-48 space-y-1 overflow-auto">
                        {choices.map((c) => (
                          <li key={c.key}>
                            <label className="flex items-center gap-2 text-sm text-gray-900">
                              <input
                                type={limit === 1 ? "radio" : "checkbox"}
                                name="keys"
                                checked={keys.includes(c.key)}
                                disabled={
                                  limit > 1 && !keys.includes(c.key) && (keys.length >= limit || thirdUnit(c.key))
                                }
                                onChange={(e) =>
                                  setKeys(
                                    limit === 1
                                      ? [c.key]
                                      : e.target.checked
                                        ? [...keys, c.key]
                                        : keys.filter((k) => k !== c.key),
                                  )
                                }
                              />
                              {c.name}
                            </label>
                          </li>
                        ))}
                      </ul>
                    </fieldset>
                  )}
                </>
              )}
            </section>
          </>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={line ? !keys[0] : !ready || !p}
            onClick={() => {
              if (line) {
                if (keys[0])
                  onAddLine?.(
                    { device: deviceId, datapoint: keys[0] },
                    p?.points[keys[0]]?.unit ?? null,
                    p?.points[keys[0]]?.name ?? keys[0],
                  );
                onClose();
                return;
              }
              if (!p || !type) return;
              if (change) {
                const built = toWidget(
                  {
                    ...fromChoice(
                      type,
                      choices.filter((c) => keys.includes(c.key)),
                      doc,
                    ),
                  },
                  0,
                  deviceId,
                  p,
                );
                onChange?.(
                  { type: built.type as DocWidget["type"], bindings: built.bindings, config: built.config },
                  deviceId,
                );
                onClose();
                return;
              }
              add(
                fromChoice(
                  type,
                  choices.filter((c) => keys.includes(c.key)),
                  doc,
                ),
                p,
              );
            }}
          >
            {change ? t("editor.change") : t("editor.add")}
          </Button>
        </div>
      </div>
      {plansOpen && planDevice.data && (
        <PlansDialog orgId={orgId} device={planDevice.data} onClose={() => setPlansOpen(false)} />
      )}
    </dialog>
  );
}
